import { useExportContext } from '@hooks/export/context/ExportContext';
import ExportContextProvider from '@hooks/export/context/ExportContextProvider';
import PerformanceSegments, { PerformanceSegmentsProps } from '@components/performance-segments/PerformanceSegments';
import { enableChartsExporting } from '@ecosystem/export-utils';
import { InsightRendererProps } from '@ecosystem/newron-client-collections';
import { CategoryLeaderboardParam } from '@models/CategoryLeaderboard';
import { renderLoading } from '@pages/Loading';
import { getCategoryLeaderboard } from '@services/CategoryLeaderboard.service';
import { getAbortController } from '@services/utils/OverviewServiceUtils';
import Store from '@state/Store';
import {
  constructDummyPeriodForV2YTDAndMATWeekly,
  getIsUsingCollectionLiveData,
  getPeriodLabel,
} from '@utils/collectionUtils/CollectionUtils';
import { exportPowerPointImages } from '@utils/collectionUtils/ExportUtils';
import { useFetchChartData } from '@utils/collectionUtils/hooks/UseFetchChartData';
import { FC, useEffect } from 'react';
import { Provider } from 'react-redux';
import PgWarningBanner from '../warning-banner/pg-warning-banner/PgWarningBanner';
import { CategoryLeaderboardDoubleBarChartInsight } from './Model';
import { getCountryCodes, getCountryOptions } from '@utils/CountryUtils';
import { WarningBannerProps } from '../warning-banner/Types';
import CountryWarningBanner from '../warning-banner/country-warning-banner/CountryWarningBanner';
import { getExploreLeaderboard } from '@services/explore/Explore.service';
import { getBaseTimePeriodForMATAndYTD, getPeriodicityForMATAndYTD } from '@utils/PeriodUtils';

enableChartsExporting();

type DoubleBarChartInsightRenderer = InsightRendererProps<CategoryLeaderboardDoubleBarChartInsight>;

const DoubleBarChartContainer: FC<DoubleBarChartInsightRenderer> = ({ insight, onLoaded, onExportReady }) => {
  const { apiParams, selectedGrowthType, factRevenue, volume, collectionDataType } = insight.data;
  const baseTimePeriod = getBaseTimePeriodForMATAndYTD(apiParams.period, apiParams.periodType);
  const { periodicity } = getPeriodicityForMATAndYTD(baseTimePeriod, apiParams.periodType);
  const { isLoading, chartData } = useFetchChartData({
    apiRequest: async () => {
      const label = await getPeriodLabel({
        clientId: apiParams.clientId,
        periodicity,
        baseTimePeriod,
        comparisonPeriod: apiParams.comparisonPeriod,
        countryIds: getCountryCodes(apiParams.countries),
        productType: apiParams.productType,
        collectionDataType: getIsUsingCollectionLiveData(apiParams.period, collectionDataType)
          ? 'live'
          : collectionDataType,
        productGroups: apiParams.productGroup,
      });

      const formatLabel = () => {
        if (/YTD|MAT/i.test(apiParams.period)) {
          const periodType = apiParams.period.match(/YTD|MAT/i)?.[0];
          return `${periodType} ${label.split(/vs/i)[0]?.trim()}`;
        }
        return label.split(/vs/i)[0]?.trim();
      };

      const getPeriodFromLabel = formatLabel() || apiParams.period;

      const isUsingCollectionLiveData = getIsUsingCollectionLiveData(getPeriodFromLabel, collectionDataType);
      const params: CategoryLeaderboardParam = {
        ...apiParams,
        period: isUsingCollectionLiveData
          ? constructDummyPeriodForV2YTDAndMATWeekly(getPeriodFromLabel, apiParams.periodType)
          : apiParams.period,
        isUsingCollectionLiveData: isUsingCollectionLiveData,
      };
      const response = apiParams.showPerformance
        ? await getExploreLeaderboard(params)
        : await getCategoryLeaderboard(params, getAbortController());
      return response.leaderboardKpis.content;
    },
    onLoaded: onLoaded,
  });

  // Export Register
  const { chartRefsIsInitialisedStatus, multipleExportCallbacks } = useExportContext();
  useEffect(() => {
    if (chartRefsIsInitialisedStatus.length === 0 || chartRefsIsInitialisedStatus.some((status) => !status)) {
      return;
    }

    const exportPng = exportPowerPointImages({ exportCallbacks: multipleExportCallbacks });
    onExportReady({ exportPng });
  }, [onExportReady, chartRefsIsInitialisedStatus, multipleExportCallbacks]);

  const props: PerformanceSegmentsProps = {
    isLoading: isLoading,
    categoryLeaderboardPg: chartData ?? [],
    selectedGrowthType: selectedGrowthType,
    setIsExportButtonDisabled: () => {}, // empty function to satisfy the type interface
    factRevenue: factRevenue,
    volume: volume,
    onExportReady: () => {}, // empty function to satisfy the type interface
    isUsingCollection: true,
    sortingOptions: apiParams.sortOption,
    setCurrentSortingOption: () => {},
    shareType: apiParams.shareType,
  };

  if (isLoading) {
    return renderLoading();
  }

  return <PerformanceSegments {...props} />;
};

const DoubleBarChartInsight: FC<DoubleBarChartInsightRenderer> = (props) => {
  const {
    apiParams: { clientId, period, periodType, countries, productGroup, productType, comparisonPeriod },
    collectionDataType,
  } = props.insight.data;

  if (countries.length > 1) {
    const warningBannerProps: WarningBannerProps = {
      clientId,
      countries: getCountryOptions(countries),
      productGroups: productGroup,
      period,
      periodicity: periodType,
      comparisonPeriod,
      productType,
      collectionDataType,
    };

    return (
      <Provider store={Store}>
        <ExportContextProvider>
          <CountryWarningBanner warningBannerProps={warningBannerProps}>
            <DoubleBarChartContainer {...props} />
          </CountryWarningBanner>
        </ExportContextProvider>
      </Provider>
    );
  }

  return (
    <Provider store={Store}>
      <ExportContextProvider>
        <PgWarningBanner baseApiParams={props.insight.data.apiParams} collectionDataType={props.insight.dataType}>
          <DoubleBarChartContainer {...props} />
        </PgWarningBanner>
      </ExportContextProvider>
    </Provider>
  );
};

export default DoubleBarChartInsight;
